import { useState } from "react";
import { Dialog } from "primereact/dialog";
import {
  adjustmentStatus, adjustmentLabels, adjustmentColors,
  formatAdjustmentTime, latestAdjustmentTime, formatAdjustmentQuantity,
} from "../../../../shared/productionInventoryAdjustments";

export function ProductionAdjustmentBadge({ records }) {
  const status = adjustmentStatus(records);
  return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${adjustmentColors[status]}`}>
    {adjustmentLabels[status]}
  </span>;
}

export default function ProductionInventoryAdjustmentHistory({ adjustments, loading, error, onRefresh, saving, onRetry, canRetry }) {
  const [open, setOpen] = useState(false);
  const completed = (adjustments || []).filter((item) => item.status === "completed");
  const latest = latestAdjustmentTime(completed);
  const hasFailures = (adjustments || []).some((item) => item.status === "failed");
  return <section className="rounded-lg border border-gray-200 bg-white p-4" aria-label="QuickBooks inventory adjustments">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold text-gray-900">QuickBooks inventory</h2>
        <div className="mt-2 flex flex-wrap items-center gap-2" aria-live="polite">
          {loading ? <span className="text-sm text-gray-600">Loading adjustment history…</span> : <ProductionAdjustmentBadge records={error ? null : adjustments} />}
          {!error && latest && <span className="text-sm text-gray-600">Last adjusted {formatAdjustmentTime(latest)}</span>}
        </div>
        {!loading && !error && Array.isArray(adjustments) && !adjustments.length &&
          <p className="mt-2 text-sm text-gray-600">No inventory deductions have been recorded for this production.</p>}
        {!error && completed.length > 0 && <p className="mt-2 text-sm text-gray-600">{completed.length} recorded ingredient deduction{completed.length === 1 ? "" : "s"}. Later quantity changes are not automatically reconciled.</p>}
        {saving && <p role="status" className="mt-2 text-sm text-indigo-700">Saving production. If completing, please wait while QuickBooks inventory is adjusted.</p>}
        {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      </div>
      <button type="button" className="rounded-md border border-indigo-300 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
        onClick={() => { setOpen(true); onRefresh(); }}>Adjustment History</button>
    </div>
    <Dialog header="QuickBooks adjustment history" visible={open} onHide={() => setOpen(false)}
      modal style={{ width: "min(70rem, 95vw)" }}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">Latest record per assembly item. Retries update the same record. Times use your local timezone.</p>
        {canRetry && hasFailures && onRetry && <button type="button"
          disabled={saving || loading}
          onClick={() => { setOpen(false); onRetry(); }}
          className="rounded bg-main-purple px-3 py-1 text-sm font-semibold text-white disabled:opacity-50">Retry failed adjustments</button>}
        <button type="button" onClick={onRefresh} disabled={loading} className="rounded border px-3 py-1 text-sm disabled:opacity-50">{loading ? "Refreshing…" : "Refresh"}</button>
      </div>
      {error ? <p role="alert" className="text-red-700">{error}</p> : loading ? <p role="status">Loading adjustment history…</p> : !adjustments?.length ?
        <p>No inventory deductions have been recorded for this production.</p> :
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead><tr>{["BOM PLU / Assembly item", "Status", "Quantity deducted", "QB stock before → after", "Adjusted at", "Record updated"].map((heading) => <th key={heading} className="whitespace-nowrap px-3 py-3 text-left">{heading}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-100">{adjustments.map((item) => <tr key={item.id}>
            <td className="px-3 py-3 font-medium">{item.sku}<span className="block text-xs text-gray-500">Assembly item #{item.assembly_item_id}</span></td>
            <td className="px-3 py-3"><ProductionAdjustmentBadge records={[item]} />{item.error && <p className="mt-1 max-w-xs whitespace-normal text-xs text-red-700">{item.error}</p>}</td>
            <td className="px-3 py-3">{item.status === "completed" ? `−${formatAdjustmentQuantity(item.quantity)}` : `Requested: ${formatAdjustmentQuantity(item.quantity)}`}</td>
            <td className="whitespace-nowrap px-3 py-3">{formatAdjustmentQuantity(item.previous_qty_on_hand)} → {formatAdjustmentQuantity(item.qty_on_hand)}</td>
            <td className="whitespace-nowrap px-3 py-3">{formatAdjustmentTime(item.adjusted_at)}</td>
            <td className="whitespace-nowrap px-3 py-3">{formatAdjustmentTime(item.updated_at)}</td>
          </tr>)}</tbody>
        </table></div>}
    </Dialog>
  </section>;
}
